import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Users as UsersIcon, Mail, Phone, MapPin, Plus, Edit, Trash2, Save, X, MessageCircle, Eye, Briefcase } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

import { getBackendUrl } from '../utils/getBackendUrl';
const BACKEND_URL = getBackendUrl();
const API = `${BACKEND_URL}/api`;

const SubContractors = ({ user }) => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    password: '',
    role: 'sub_contractor',
    phone: '',
    address: '',
    specialty: '',
    hourly_rate: ''
  });

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await axios.get(`${API}/users`);
        if (Array.isArray(response.data)) {
          // Filter only sub-contractors
          setUsers(response.data.filter(u => u.role === 'sub_contractor'));
        } else {
          setUsers([]);
        }
      } catch (error) {
        console.error('Failed to fetch users');
        setUsers([]);
      }
    };

    if (user.role === 'admin' || user.role === 'supervisor') {
      fetchUsers();
    }
  }, [user.role]);

  const refetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      if (Array.isArray(response.data)) {
        setUsers(response.data.filter(u => u.role === 'sub_contractor'));
      } else {
        setUsers([]);
      }
    } catch (error) {
      console.error('Failed to fetch users');
      setUsers([]);
    }
  };

  // Admin-only access
  if (user.role !== 'admin') {
    return (
      <div className="min-h-screen bg-gray-50 pb-20 pt-20 flex items-center justify-center">
        <div className="text-center">
          <Briefcase className="w-16 h-16 mx-auto mb-4 text-gray-400" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h1>
          <p className="text-gray-600">Only administrators can access the Sub-Contractors page.</p>
        </div>
      </div>
    );
  }

  const handleAddUser = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/users`, { ...formData, role: 'sub_contractor' });
      toast.success('Sub-contractor added successfully!');
      setIsAddDialogOpen(false);
      resetForm();
      refetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add sub-contractor');
    }
  };

  const handleUpdateUser = async (userId) => {
    try {
      const updateData = {
        name: editingUser.name,
        email: editingUser.email,
        phone: editingUser.phone,
        address: editingUser.address,
        specialty: editingUser.specialty,
        hourly_rate: editingUser.hourly_rate
      };
      
      await axios.put(`${API}/admin/users/${userId}`, updateData);
      toast.success('Sub-contractor updated successfully!');
      setEditingUser(null);
      refetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update sub-contractor');
    }
  };

  const handleResetPassword = async (userId, userName) => {
    const newPassword = prompt(`Enter new password for ${userName}:`);
    if (!newPassword) return;
    
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    try {
      const formData = new FormData();
      formData.append('new_password', newPassword);
      
      await axios.put(`${API}/admin/users/${userId}/password`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Password reset successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this sub-contractor? This action cannot be undone.')) return;
    
    try {
      await axios.delete(`${API}/admin/users/${userId}`);
      toast.success('Sub-contractor deleted successfully!');
      refetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete sub-contractor');
    }
  };

  const startEditing = (userToEdit) => {
    setEditingUser({ ...userToEdit });
  };

  const cancelEditing = () => {
    setEditingUser(null);
  };

  const resetForm = () => {
    setFormData({
      email: '',
      name: '',
      password: '',
      role: 'sub_contractor',
      phone: '',
      address: '',
      specialty: '',
      hourly_rate: ''
    });
  };

  const getLastOnlineStatus = (lastSeen, isOnline) => {
    if (!lastSeen) return { text: 'Never logged in', color: 'text-slate-500', dot: 'bg-slate-500' };
    
    if (isOnline) return { text: 'Online now', color: 'text-green-400', dot: 'bg-green-500' };
    
    const now = new Date();
    const lastSeenDate = new Date(lastSeen);
    const diffMs = now - lastSeenDate;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return { text: 'Just now', color: 'text-green-400', dot: 'bg-green-500' };
    if (diffMins < 5) return { text: `${diffMins}m ago`, color: 'text-green-400', dot: 'bg-green-500' };
    if (diffMins < 60) return { text: `${diffMins}m ago`, color: 'text-yellow-400', dot: 'bg-yellow-500' };
    if (diffHours < 24) return { text: `${diffHours}h ago`, color: 'text-orange-400', dot: 'bg-orange-500' };
    if (diffDays < 7) return { text: `${diffDays}d ago`, color: 'text-slate-400', dot: 'bg-slate-500' };
    return { text: lastSeenDate.toLocaleDateString('en-CA', { timeZone: 'America/Toronto' }), color: 'text-slate-500', dot: 'bg-slate-500' };
  };

  const canSeeOnline = user?.email === 'admin@dawalconnect.ca';

  // Sort users: online first, then by last seen
  const sortedUsers = [...users].sort((a, b) => {
    if (a.is_online && !b.is_online) return -1;
    if (!a.is_online && b.is_online) return 1;
    if (a.last_seen && b.last_seen) {
      return new Date(b.last_seen) - new Date(a.last_seen);
    }
    return 0;
  });

  return (
    <div className="min-h-screen bg-slate-900 pb-20 pt-20" data-testid="subcontractors-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-white flex items-center gap-3">
              <Briefcase className="w-8 h-8 text-indigo-400" />
              Sub-Contractors
            </h1>
            <p className="text-slate-400 mt-1">Manage your sub-contractor team members</p>
          </div>
          
          <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-gradient-to-r from-orange-500 to-orange-600 text-white">
                <Plus className="w-4 h-4 mr-2" />
                Add Sub-Contractor
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white text-gray-900 border-gray-300 max-w-lg">
              <DialogHeader>
                <DialogTitle className="text-gray-900 text-xl">Add New Sub-Contractor</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleAddUser} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <Label className="text-gray-700">Name *</Label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      placeholder="Full name"
                      className="text-gray-900 bg-white border-gray-300"
                    />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-gray-700">Email *</Label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                      placeholder="email@example.com"
                      className="text-gray-900 bg-white border-gray-300"
                    />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-gray-700">Password *</Label>
                    <Input
                      type="password"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      required
                      placeholder="Min 6 characters"
                      className="text-gray-900 bg-white border-gray-300"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-700">Phone</Label>
                    <Input
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="+1 (555) 000-0000"
                      className="text-gray-900 bg-white border-gray-300"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-700">Specialty</Label>
                    <Input
                      value={formData.specialty}
                      onChange={(e) => setFormData({ ...formData, specialty: e.target.value })}
                      placeholder="e.g., Electrical"
                      className="text-gray-900 bg-white border-gray-300"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-700">Hourly Rate ($)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.hourly_rate}
                      onChange={(e) => setFormData({ ...formData, hourly_rate: e.target.value })}
                      placeholder="0.00"
                      className="text-gray-900 bg-white border-gray-300"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-700">Address</Label>
                    <Input
                      value={formData.address}
                      onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                      placeholder="Full address"
                      className="text-gray-900 bg-white border-gray-300"
                    />
                  </div>
                </div>
                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setIsAddDialogOpen(false)} className="text-gray-700 border-gray-300">
                    Cancel
                  </Button>
                  <Button type="submit" className="bg-gradient-to-r from-orange-500 to-orange-600 text-white">
                    Add Sub-Contractor
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Stats */}
        <div className="mb-6 flex items-center gap-4 text-sm text-slate-400">
          <span className="px-3 py-1 bg-slate-800 rounded-lg">
            Total: <span className="text-white font-semibold">{users.length}</span>
          </span>
          {canSeeOnline && (
            <span className="px-3 py-1 bg-slate-800 rounded-lg">
              Online: <span className="text-green-400 font-semibold">{users.filter(u => u.is_online).length}</span>
            </span>
          )}
        </div>

        {/* Sort Indicator - Walter only */}
        {canSeeOnline && (
          <div className="mb-4 flex items-center gap-2 text-sm text-slate-400">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12" />
            </svg>
            <span>Sorted by online status (most recent first)</span>
          </div>
        )}

        {/* Users Grid */}
        {sortedUsers.length === 0 ? (
          <div className="glass-card p-12 text-center">
            <Briefcase className="w-16 h-16 mx-auto mb-4 text-slate-500" />
            <h3 className="text-xl font-semibold text-white mb-2">No Sub-Contractors Found</h3>
            <p className="text-slate-400 mb-4">Add your first sub-contractor to get started.</p>
            <Button onClick={() => setIsAddDialogOpen(true)} className="bg-gradient-to-r from-orange-500 to-orange-600">
              <Plus className="w-4 h-4 mr-2" />
              Add Sub-Contractor
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {sortedUsers.map((member) => (
              <div key={member.id} className="glass-card p-6 slide-up" data-testid={`subcontractor-card-${member.id}`}>
                {editingUser && editingUser.id === member.id ? (
                  // Edit Mode
                  <div className="space-y-4">
                    <div>
                      <Label>Name</Label>
                      <Input
                        value={editingUser.name}
                        onChange={(e) => setEditingUser({ ...editingUser, name: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label>Email</Label>
                      <Input
                        type="email"
                        value={editingUser.email}
                        onChange={(e) => setEditingUser({ ...editingUser, email: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label>Phone</Label>
                      <Input
                        value={editingUser.phone || ''}
                        onChange={(e) => setEditingUser({ ...editingUser, phone: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label>Specialty</Label>
                      <Input
                        value={editingUser.specialty || ''}
                        onChange={(e) => setEditingUser({ ...editingUser, specialty: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label>Hourly Rate</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={editingUser.hourly_rate || ''}
                        onChange={(e) => setEditingUser({ ...editingUser, hourly_rate: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label>Address</Label>
                      <Input
                        value={editingUser.address || ''}
                        onChange={(e) => setEditingUser({ ...editingUser, address: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          onClick={() => handleUpdateUser(member.id)}
                          className="flex-1 bg-gradient-to-r from-green-500 to-green-600"
                        >
                          <Save className="w-4 h-4 mr-2" />
                          Save
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={cancelEditing}
                          className="flex-1"
                        >
                          <X className="w-4 h-4 mr-2" />
                          Cancel
                        </Button>
                      </div>
                      <Button
                        size="sm"
                        onClick={() => handleResetPassword(member.id, member.name)}
                        className="w-full bg-yellow-600 hover:bg-yellow-700"
                      >
                        Reset Password
                      </Button>
                    </div>
                  </div>
                ) : (
                  // View Mode
                  <>
                    {/* User Name - Prominent Display at Top */}
                    <div className="mb-4">
                      <div className="inline-block px-4 py-2 bg-gradient-to-r from-indigo-600 to-indigo-700 rounded-lg shadow-md">
                        <div className="flex items-center gap-2">
                          <h3 className="text-base font-bold text-white uppercase tracking-wider">
                            {member.name}
                          </h3>
                          {canSeeOnline && member.is_online && (
                            <span className="text-xs text-green-300 font-semibold">● Online</span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-4 mb-4">
                      <div className="relative">
                        <Avatar className="w-16 h-16">
                          <AvatarImage src={member.profile_picture ? `${BACKEND_URL}${member.profile_picture}` : undefined} />
                          <AvatarFallback className="bg-indigo-500 text-white text-xl">
                            {member.name.charAt(0).toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                        {canSeeOnline && member.is_online && (
                          <div className="absolute bottom-0 right-0 w-4 h-4 bg-green-500 border-2 border-white rounded-full"></div>
                        )}
                      </div>
                      <div className="flex-1">
                        <span className="inline-block px-3 py-1 rounded-full text-xs font-semibold bg-indigo-500/20 text-indigo-300">
                          SUB-CONTRACTOR
                        </span>
                        {member.specialty && (
                          <p className="text-xs text-slate-400 mt-1">
                            <Briefcase className="w-3 h-3 inline mr-1" />
                            {member.specialty}
                          </p>
                        )}
                        {/* Last Online Status - Walter only */}
                        {canSeeOnline && (() => {
                          const status = getLastOnlineStatus(member.last_seen, member.is_online);
                          return (
                            <div className="flex items-center gap-2 mt-2">
                              <div className={`w-2 h-2 rounded-full ${status.dot}`}></div>
                              <span className={`text-xs ${status.color} font-medium`}>
                                {status.text}
                              </span>
                            </div>
                          );
                        })()}
                      </div>
                    </div>

                    <div className="space-y-2 mb-4">
                      <div className="flex items-center gap-2 text-sm">
                        <Mail className="w-4 h-4 text-slate-400" />
                        <span className="text-slate-300">{member.email}</span>
                      </div>
                      {member.phone && (
                        <div className="flex items-center gap-2 text-sm">
                          <Phone className="w-4 h-4 text-slate-400" />
                          <span className="text-slate-300">{member.phone}</span>
                        </div>
                      )}
                      {member.hourly_rate && (
                        <div className="flex items-center gap-2 text-sm">
                          <span className="text-slate-400">$</span>
                          <span className="text-slate-300">{member.hourly_rate}/hr</span>
                        </div>
                      )}
                      {member.address && (
                        <div className="flex items-center gap-2 text-sm">
                          <MapPin className="w-4 h-4 text-slate-400" />
                          <span className="text-slate-300 truncate">{member.address}</span>
                        </div>
                      )}
                    </div>

                    {/* Action Buttons */}
                    <div className="flex gap-2 pt-4 border-t border-slate-700">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => navigate(`/sub-dashboard/${member.id}`)}
                        className="flex-1"
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        View
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => startEditing(member)}
                        className="flex-1"
                      >
                        <Edit className="w-4 h-4 mr-1" />
                        Edit
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDeleteUser(member.id)}
                        className="text-red-400 hover:text-red-300 hover:bg-red-900/20"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SubContractors;
